import { FC, useState } from "react";
import { gql } from "@apollo/client";
import { Button } from "@src/lib/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Textarea } from "@src/lib/ui/Textarea";
import { useToast } from "@src/lib/hooks/useToast";
import {
  useSendSeparateChatMessagesDialogUsersQuery,
  useSendSeparateChatMessagesMutation,
} from "./SendSeparateChatMessagesDialog.generated";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userIds: number[];
  onSubmit?: () => void;
};

export const getSendSeparateChatMessagesTitle = (n: number): string =>
  n === 1 ? "Send message" : "Send messages";

export const getSendSeparateChatMessagesDescription = (
  n: number,
  recipientName: string | undefined,
): string =>
  n === 1
    ? `This will send a direct message to ${recipientName}.`
    : `${n} direct messages will be sent, one to each selected user.`;

const SendSeparateChatMessagesDialog: FC<Props> = ({
  open,
  onOpenChange,
  userIds,
  onSubmit,
}) => {
  const { addToast, addErrorToast } = useToast();
  const [text, setText] = useState("");
  const n = userIds.length;

  const { data } = useSendSeparateChatMessagesDialogUsersQuery({
    variables: {
      input: {
        userIds,
        allLocations: false,
        allManagers: false,
        allTrainees: false,
      },
    },
    skip: n === 0,
  });
  const recipientName = data?.GetUsersForUserSelection[0]?.name;

  const [sendSeparateChatMessages, { loading }] =
    useSendSeparateChatMessagesMutation({
      variables: { input: { text, recipientUserIds: userIds } },
      onError: () => {
        addErrorToast({ callsite: "send_separate_chat_messages" });
      },
      onCompleted: (result) => {
        if (!result.sendSeparateChatMessages.success) {
          addErrorToast({ callsite: "send_separate_chat_messages" });
          return;
        }
        addToast({
          message: n === 1 ? "Message sent!" : `${n} messages sent!`,
        });
        onSubmit?.();
        onOpenChange(false);
      },
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-[440px] flex-col gap-0 p-0">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle>{getSendSeparateChatMessagesTitle(n)}</DialogTitle>
          <DialogDescription>
            {getSendSeparateChatMessagesDescription(n, recipientName)}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col p-4">
          <Textarea
            className="min-h-[108px]"
            value={text}
            placeholder="Write a message"
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <DialogFooter className="p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={text.trim().length === 0 || userIds.length === 0}
            loading={loading}
            onClick={() => sendSeparateChatMessages()}
          >
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendSeparateChatMessagesDialog;

gql`
  query SendSeparateChatMessagesDialogUsers($input: UserSelectionInput!) {
    GetUsersForUserSelection(input: $input) {
      id
      name
    }
  }

  mutation SendSeparateChatMessages($input: SendSeparateChatMessagesInput!) {
    sendSeparateChatMessages(input: $input) {
      success
      error {
        code
      }
    }
  }
`;
